import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { Bot, Loader2 } from 'lucide-react';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth, useLogin } from '@/hooks/useAuth';

/**
 * Login — the one public route before the shell loads. Controlled inputs
 * (no react-hook-form in the scaffold to keep deps minimal). Shows a
 * bootstrap banner pointing at the CLI reset path per §4.1.
 */
export default function Login() {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const login = useLogin();
  const { isLoggedIn, mustChangePassword, isLoading } = useAuth();

  // Clear any stale error when the user edits the form.
  useEffect(() => {
    if (login.isError) login.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, password]);

  if (isLoading) return <FullPageSpinner />;
  if (isLoggedIn) {
    return <Navigate to={mustChangePassword ? '/first-login' : '/'} replace />;
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login.mutate({ username, password });
  };

  const errorMessage =
    login.error instanceof ApiError
      ? login.error.detail ?? login.error.title
      : login.error?.message;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-[380px]">
        <CardHeader className="items-center space-y-3 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Bot className="h-5 w-5" aria-hidden />
          </div>
          <CardTitle>AgentHub Mission Control</CardTitle>
          <p className="text-sm text-muted-foreground">
            Sign in to manage your agents
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                required
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {errorMessage ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              >
                {errorMessage}
              </div>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={login.isPending || !username || !password}
            >
              {login.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Forgot password? Run{' '}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">
              python -m backend.cli reset-admin
            </code>{' '}
            on the server.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}
